import { DataType, FormDialogMode, useNotify } from '@globaleris/ui.library'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'

interface PricePeriodFormDialogProps {
  rounding?: number
  formMode: FormDialogMode
  open: boolean
  onClose: (formData: any) => void
  inputData?: any
}

export const PricePeriodFormDialog: React.FC<PricePeriodFormDialogProps> = ({
  formMode,
  open,
  onClose,
  inputData,
  rounding = 6
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'pricePeriodFormDialog'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { info, error } = useNotify()

  // #region Handlers Starts
  const onFormCloseHandler = async (formData: any) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(pricePeriodFormDefinition?.sections!, formData)) {
        error(t('pricePeriodFormDialog:notifications.incompleteForm'))
        return
      }
    }
    closeForm()
    onClose(formData)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts

  const pricePeriodFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    () => ({
      id: 'pricePeriodForm',
      sections: [
        {
          inputs: [
            {
              name: 'startDate',
              label: t('pricePeriodFormDialog:forms.inputs.startDate'),
              type: DataType.DATE,
              required: true
            },
            {
              name: 'endDate',
              label: t('pricePeriodFormDialog:forms.inputs.endDate'),
              type: DataType.DATE
            }
          ]
        },
        {
          inputs: [
            {
              name: 'period1.value',
              label: t('pricePeriodFormDialog:forms.inputs.period1'),
              type: DataType.NUMBER,
              required: true,
              customDecimalScale: rounding,
              clearable: true
            },
            {
              name: 'period2.value',
              label: t('pricePeriodFormDialog:forms.inputs.period2'),
              type: DataType.NUMBER,
              customDecimalScale: rounding,
              clearable: true
            },
            {
              name: 'period3.value',
              label: t('pricePeriodFormDialog:forms.inputs.period3'),
              type: DataType.NUMBER,
              customDecimalScale: rounding,
              clearable: true
            },
            {
              name: 'period4.value',
              label: t('pricePeriodFormDialog:forms.inputs.period4'),
              type: DataType.NUMBER,
              customDecimalScale: rounding,
              clearable: true
            },
            {
              name: 'period5.value',
              label: t('pricePeriodFormDialog:forms.inputs.period5'),
              type: DataType.NUMBER,
              customDecimalScale: rounding,
              clearable: true
            },
            {
              name: 'period6.value',
              label: t('pricePeriodFormDialog:forms.inputs.period6'),
              type: DataType.NUMBER,
              customDecimalScale: rounding,
              clearable: true
            }
          ]
        }
      ]
    }),
    [rounding]
  )

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    open &&
      openForm({
        definition: pricePeriodFormDefinition,
        title:
          formMode === FormDialogMode.EDIT
            ? t('pricePeriodFormDialog:forms.edit.title')
            : t('pricePeriodFormDialog:forms.add.title'),
        mode: formMode,
        isWaiting: false,
        ...(formMode === FormDialogMode.EDIT && { inputData: structuredClone(inputData) }),
        onClose: onFormCloseHandler
      })
  }, [open])

  useEffect(() => {
    updateFormDefinition(pricePeriodFormDefinition)
  }, [pricePeriodFormDefinition])

  // #endregion Hooks Ends

  return (
    <>
      <CustomFormDialog {...formProps} />
    </>
  )
}
